/**
 * Countdown Store - Zustand store for countdown timer state management.
 *
 * Manages active and expired countdowns with offline-first persistence
 * via CountdownService. Active countdowns are sorted by nearest target date;
 * expired countdowns are sorted by most recently expired first.
 *
 * @see Requirements 9.1, 9.2, 9.5, 9.7
 */

import { create } from 'zustand';
import type { CountdownTimer } from '@/types';
import type { CreateCountdownInput, UpdateCountdownInput } from '@/services/countdownService';
import {
  getActiveCountdowns,
  getExpiredCountdowns,
  createCountdown as createCountdownService,
  updateCountdown as updateCountdownService,
  deleteCountdown as deleteCountdownService,
  reorderCountdowns as reorderCountdownsService,
  checkExpiredCountdowns,
  cleanupExpired,
} from '@/services/countdownService';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface CountdownState {
  /** Active (non-expired) countdowns, sorted by nearest target date. */
  activeCountdowns: CountdownTimer[];
  /** Expired countdowns, sorted by most recently expired first. */
  expiredCountdowns: CountdownTimer[];
  /** Currently selected countdown ID for detail view. */
  selectedCountdownId: string | null;
  /** Whether a countdown operation is in progress. */
  isLoading: boolean;
  /** Last error message from a countdown operation. */
  error: string | null;
}

export interface CountdownActions {
  /** Load both active and expired countdowns from local storage. */
  loadCountdowns: (userId: string) => Promise<void>;
  /** Create a new countdown timer. */
  createCountdown: (input: CreateCountdownInput, userId: string) => Promise<CountdownTimer>;
  /** Update an existing countdown timer. */
  updateCountdown: (countdownId: string, updates: UpdateCountdownInput, userId: string) => Promise<void>;
  /** Delete a countdown timer. */
  deleteCountdown: (countdownId: string, userId: string) => Promise<void>;
  /** Reorder countdowns by providing ordered IDs. */
  reorderCountdowns: (orderedIds: string[], userId: string) => Promise<void>;
  /** Check for expired countdowns and reload state. */
  checkExpired: (userId: string) => Promise<void>;
  /** Clean up countdowns expired more than 30 days ago and reload. */
  cleanupOldExpired: (userId: string) => Promise<void>;
  /** Select a countdown for detail view (or deselect with null). */
  selectCountdown: (countdownId: string | null) => void;
}

export type CountdownStore = CountdownState & CountdownActions;

// ─── Store ───────────────────────────────────────────────────────────────────

export const useCountdownStore = create<CountdownStore>((set) => ({
  // ── State ──────────────────────────────────────────────────────────────────
  activeCountdowns: [],
  expiredCountdowns: [],
  selectedCountdownId: null,
  isLoading: false,
  error: null,

  // ── Actions ────────────────────────────────────────────────────────────────

  loadCountdowns: async (userId: string) => {
    set({ isLoading: true, error: null });
    try {
      const [active, expired] = await Promise.all([
        getActiveCountdowns(userId),
        getExpiredCountdowns(userId),
      ]);
      set({
        activeCountdowns: active,
        expiredCountdowns: expired,
        isLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load countdowns';
      set({ isLoading: false, error: message });
    }
  },

  createCountdown: async (input: CreateCountdownInput, userId: string) => {
    set({ error: null });
    try {
      const countdown = await createCountdownService(input, userId);
      // Reload to get properly sorted lists
      const [active, expired] = await Promise.all([
        getActiveCountdowns(userId),
        getExpiredCountdowns(userId),
      ]);
      set({ activeCountdowns: active, expiredCountdowns: expired });
      return countdown;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create countdown';
      set({ error: message });
      throw error;
    }
  },

  updateCountdown: async (countdownId: string, updates: UpdateCountdownInput, userId: string) => {
    set({ error: null });
    try {
      await updateCountdownService(countdownId, updates, userId);
      // Reload to get properly sorted lists
      const [active, expired] = await Promise.all([
        getActiveCountdowns(userId),
        getExpiredCountdowns(userId),
      ]);
      set({ activeCountdowns: active, expiredCountdowns: expired });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update countdown';
      set({ error: message });
      throw error;
    }
  },

  deleteCountdown: async (countdownId: string, userId: string) => {
    set({ error: null });
    try {
      await deleteCountdownService(countdownId, userId);
      // Reload to get properly sorted lists
      const [active, expired] = await Promise.all([
        getActiveCountdowns(userId),
        getExpiredCountdowns(userId),
      ]);
      set((state) => ({
        activeCountdowns: active,
        expiredCountdowns: expired,
        // Deselect if the deleted countdown was selected
        selectedCountdownId:
          state.selectedCountdownId === countdownId ? null : state.selectedCountdownId,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete countdown';
      set({ error: message });
      throw error;
    }
  },

  reorderCountdowns: async (orderedIds: string[], userId: string) => {
    set({ error: null });
    try {
      await reorderCountdownsService(orderedIds, userId);
      // Reload to reflect new order
      const active = await getActiveCountdowns(userId);
      set({ activeCountdowns: active });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to reorder countdowns';
      set({ error: message });
      throw error;
    }
  },

  checkExpired: async (userId: string) => {
    set({ error: null });
    try {
      await checkExpiredCountdowns(userId);
      // Reload both lists since countdowns may have moved from active to expired
      const [active, expired] = await Promise.all([
        getActiveCountdowns(userId),
        getExpiredCountdowns(userId),
      ]);
      set({ activeCountdowns: active, expiredCountdowns: expired });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to check expired countdowns';
      set({ error: message });
    }
  },

  cleanupOldExpired: async (userId: string) => {
    set({ error: null });
    try {
      await cleanupExpired(userId);
      // Reload expired list since items were removed
      const expired = await getExpiredCountdowns(userId);
      set({ expiredCountdowns: expired });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to cleanup expired countdowns';
      set({ error: message });
    }
  },

  selectCountdown: (countdownId: string | null) => {
    set({ selectedCountdownId: countdownId });
  },
}));
